import React, { createContext, useContext, PropsWithChildren } from 'react';
import { notification } from 'antd';
import type { NotificationArgsProps } from 'antd';
import { t } from "i18next";

export type CustomPosition =
  | 'top-right'
  | 'top'
  | 'top-left'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left';

export interface NotificationOptions {
  autoClose?: boolean;
  close_time?: number; // Đơn vị: giây
  position?: CustomPosition;
}

type AntdPlacement = NotificationArgsProps['placement'];

export interface NotificationContextType {
  success: (message: string, title?: string, options?: NotificationOptions) => void;
  info: (message: string, title?: string, options?: NotificationOptions) => void;
  warning: (message: string, title?: string, options?: NotificationOptions) => void;
  error: (message: string, title?: string, options?: NotificationOptions) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider = ({ children }: PropsWithChildren) => {
  const [api, contextHolder] = notification.useNotification();

  const mapPosition = (pos?: CustomPosition): AntdPlacement => {
    switch (pos) {
      case 'top-left': return 'topLeft';
      case 'top-right': return 'topRight';
      case 'bottom-left': return 'bottomLeft';
      case 'bottom-right': return 'bottomRight';
      case 'top': return 'top';
      case 'bottom': return 'bottom';
      default: return 'topRight';
    }
  };

  const triggerNotification = (
    type: 'success' | 'info' | 'warning' | 'error',
    message: string,
    title: string,
    options?: NotificationOptions
  ) => {
    const { autoClose = true, close_time = 4, position = 'top-right' } = options || {};

    const config: NotificationArgsProps = {
      message: title,
      description: message,
      duration: autoClose ? close_time : 0,
      placement: mapPosition(position),
    };

    api[type](config);
  };

  const notifyValue: NotificationContextType = {
    success: (message, title, options) => triggerNotification('success', message, title ? title : t('notification.Success'), options),
    info: (message, title, options) => triggerNotification('info', message, title ? title : t('notification.Info'), options),
    warning: (message, title, options) => triggerNotification('warning', message, title ? title : t('notification.Warning'), options),
    error: (message, title, options) => triggerNotification('error', message, title ? title : t('notification.Error'), options),
  };

  return (
    <NotificationContext.Provider value={notifyValue}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useAppNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useAppNotification phải được sử dụng bên trong NotificationProvider');
  }
  return context;
};